import "react-dates-17/initialize";
import React, { Fragment, useRef } from "react";
import { PatternFormat } from "react-number-format";
import { DayPickerRangeController } from "react-dates-17";
import { Row, Col, Modal, ModalBody } from "reactstrap";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import { ButtonWithAction, RadioButton } from "@thecb/components";
import CalendarIcon from "./CalendarIcon";
import PresetButton from "./PresetButton";
import { noop } from "@/util/common";
import {
  beforeMaskedValueChange,
  DATE_MASK_ARRAY,
  SEARCH_DATES,
  getSearchDatesPresets,
} from "./Search.dates.util";
import {
  DateInputDivider,
  DateInput,
  DatesRow,
  DateInputIconWrapper,
  CalendarActionsRow,
  ClearDatesWrapper,
  DateFilterRow,
  FilterDatesLabel,
  FilterType,
} from "./DatePicker.styled";
import {
  TRANSACTION_DATE_FILTER,
  FUNDED_TIMESTAMP_FILTER,
} from "@/components/transactionSearchPage/TransactionSearchPage.state";
// eslint-disable-next-line
import moment from "moment-timezone";

const DATE_FILTER_RADIOS = {
  transaction: { name: "transaction-date", label: "Transaction date" },
  funded: { name: "funded-date", label: "Funded date" },
};
const radioInputId = (name) => `radio-${name}`;

const DatePicker = ({
  clearDates,
  closeDatesModal = noop,
  closeOnEnter = false,
  onApply = null,
  onResetRange = null,
  returnFocusRef = null,
  onClose = noop,
  changeMonth,
  setPresetDate,
  changeCalendarDates,
  changeInputStartDate,
  changeInputEndDate,
  blurInputStartDate,
  blurInputEndDate,
  focusInputStart,
  focusInputEnd,
  focusedInput,
  openDatesModal,
  search: { isOpen, input, calendar, startPlaceholder, endPlaceholder, calendarKey },
  visibleMonth,
  selectDateFilter,
  selectedDateFilter,
  isCitybaseAdmin,
}) => {
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);
  const transactionRadioRef = useRef(null);
  const fundedRadioRef = useRef(null);
  const [isStartFocused, setIsStartFocused] = React.useState(false);
  const [isEndFocused, setIsEndFocused] = React.useState(false);
  const [focusedRadio, setFocusedRadio] = React.useState(null);
  const showStartDatePlaceholder = !input.startDate && !isStartFocused;
  const showEndDatePlaceholder = !input.endDate && !isEndFocused;

  const setFocus = () => {
    if (returnFocusRef) {
      transactionRadioRef.current?.focus();
      return;
    }
    focusedInput === SEARCH_DATES.START
      ? inputStartRef?.current?.focus()
      : inputEndRef?.current?.focus();
  };

  const clearFocus = () => {
    setTimeout(() => {
      inputStartRef?.current?.blur();
      inputEndRef?.current?.blur();
    });
  };

  const closedByEnterRef = useRef(false);

  const enterCloseTimeoutRef = useRef(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const handleDocumentKeyDown = (event) => {
      if (event.defaultPrevented) return;
      // Escape just dismisses: no apply, no reset. The pending Enter close is
      // cancelled too, in case both keys land in the same tick.
      if (event.key === "Escape") {
        clearTimeout(enterCloseTimeoutRef.current);
        closeDatesModal();
        return;
      }
      if (!closeOnEnter || event.key !== "Enter") return;
      closedByEnterRef.current = true;
      enterCloseTimeoutRef.current = setTimeout(() => {
        closeDatesModal();
        onApply?.();
      });
    };
    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
      clearTimeout(enterCloseTimeoutRef.current);
    };
  }, [closeOnEnter, isOpen, closeDatesModal, onApply]);

  /**
   * handleRadioKeyDown
   *
   * Arrow keys move between the two date filter radios and select as they
   * move, the way a native radio group behaves. The library's RadioButton
   * never gives its inputs a shared `name` attribute, so the browser does not
   * group them and this has to be done by hand.
   *
   * @param {object} event - The React keyboard event.
   * @returns {undefined}
   */
  const handleRadioKeyDown = (event) => {
    const keys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const goingToFunded = event.target === transactionRadioRef.current;
    selectDateFilter(
      goingToFunded ? FUNDED_TIMESTAMP_FILTER : TRANSACTION_DATE_FILTER
    );
    (goingToFunded ? fundedRadioRef : transactionRadioRef).current?.focus();
  };

  /**
   * handleResetClick
   *
   * The Reset button: closes the calendar and hands the reset to the host,
   * which restores whatever range it considers the default and decides
   * whether to search (WCHK-3083 review).
   *
   * @returns {undefined}
   */
  const handleResetClick = () => {
    clearTimeout(enterCloseTimeoutRef.current);
    closeDatesModal();
    onResetRange();
  };

  /**
   * handleApplyClick
   *
   * The Apply button: closes the calendar and applies the selected range,
   * the same as the Enter key's close-and-apply path.
   *
   * @returns {undefined}
   */
  const handleApplyClick = () => {
    clearTimeout(enterCloseTimeoutRef.current);
    closeDatesModal();
    onApply?.();
  };

  const checkIsAllowed = ({ formattedValue }) => {
    const [month, day, year] = formattedValue.split("/");
    const isValidDay = new RegExp(
      /[[0|d][1-9|d]|[[1-2|d][0-9|d]|[[3|d][0-1|d]/
    ).test(day);
    const isValidMonth = new RegExp(/[[0|m][1-9|m]|[[1|m][0-2|m]/).test(month);
    const isValidYear = new RegExp(/[[0-2|y][\d|y][\d|y][\d|y]/).test(year);
    return isValidDay && isValidMonth && isValidYear;
  };

  return (
    <Fragment>
      <DatesRow>
        <DateInputIconWrapper>
          <CalendarIcon onClick={openDatesModal} data-token={SEARCH_DATES.START} />
        </DateInputIconWrapper>
        <DateInput>
          <PatternFormat
            data-testid="Start date"
            aria-label="Start date"
            allowEmptyFormatting={true}
            className="form-control"
            data-token={SEARCH_DATES.START}
            data-qa="Start date"
            format={showStartDatePlaceholder ? startPlaceholder : "##/##/####"}
            getInputRef={inputStartRef}
            isAllowed={checkIsAllowed}
            mask={DATE_MASK_ARRAY}
            onClick={(e) => {
              e.preventDefault();
              if (!isOpen) openDatesModal(e);
            }}
            onFocus={(e) => {
              e.preventDefault();
              if (isOpen) {
                focusInputStart(e);
                setIsStartFocused(true);
              }
            }}
            onBlur={(e) => {
              e.preventDefault();
              setIsStartFocused(false);
              if (isOpen) {
                blurInputStartDate(e.target.value, SEARCH_DATES.START);
              }
            }}
            onChange={(e) => {
              changeInputStartDate(e.target.value, SEARCH_DATES.START);
            }}
            onValueChange={beforeMaskedValueChange}
            placeholder={startPlaceholder}
            value={input.startDate}
          />
        </DateInput>
        <DateInputDivider />
        <DateInputIconWrapper>
          <CalendarIcon onClick={openDatesModal} data-token={SEARCH_DATES.END} />
        </DateInputIconWrapper>
        <DateInput>
          <PatternFormat
            data-testid="End date"
            aria-label="End date"
            allowEmptyFormatting={true}
            className="form-control"
            data-token={SEARCH_DATES.END}
            data-qa="End date"
            format={showEndDatePlaceholder ? endPlaceholder : "##/##/####"}
            getInputRef={inputEndRef}
            isAllowed={checkIsAllowed}
            mask={DATE_MASK_ARRAY}
            onClick={(e) => {
              e.preventDefault();
              if (!isOpen) openDatesModal(e);
            }}
            onFocus={(e) => {
              e.preventDefault();
              if (isOpen) {
                focusInputEnd(e);
                setIsEndFocused(true);
              }
            }}
            onBlur={(e) => {
              e.preventDefault();
              setIsEndFocused(false);
              if (isOpen) {
                blurInputEndDate(e.target.value, SEARCH_DATES.END);
              }
            }}
            onChange={(e) => {
              changeInputEndDate(e.target.value, SEARCH_DATES.END);
            }}
            onValueChange={beforeMaskedValueChange}
            placeholder={endPlaceholder}
            value={input.endDate}
          />
        </DateInput>
      </DatesRow>
      <Modal
        fade={false}
        isOpen={isOpen}
        onOpened={setFocus}
        onClosed={() => {
          clearFocus();
          if (closedByEnterRef.current) {
            closedByEnterRef.current = false;
            setTimeout(() => (returnFocusRef ?? inputStartRef)?.current?.focus());
          }
          onClose();
        }}
        className={`modal-search modal-search-dates ${
          isCitybaseAdmin && `admin-modal-search-dates`
        }`}
      >
        <ModalBody>
          <DateFilterRow>
            <FilterDatesLabel>Filter dates by:</FilterDatesLabel>
            <FilterType
              onClick={() => selectDateFilter(TRANSACTION_DATE_FILTER)}
              data-qa="Transaction date"
            >
              <RadioButton
                name={DATE_FILTER_RADIOS.transaction.name}
                radioOn={selectedDateFilter === TRANSACTION_DATE_FILTER}
                radioFocused={focusedRadio === DATE_FILTER_RADIOS.transaction.name}
                checked={selectedDateFilter === TRANSACTION_DATE_FILTER}
                toggleRadio={() => selectDateFilter(TRANSACTION_DATE_FILTER)}
                tabIndex="0"
                inputRef={transactionRadioRef}
                onFocus={() => setFocusedRadio(DATE_FILTER_RADIOS.transaction.name)}
                onBlur={() => setFocusedRadio(null)}
                onKeyDown={handleRadioKeyDown}
              />
              <label htmlFor={radioInputId(DATE_FILTER_RADIOS.transaction.name)}>
                {DATE_FILTER_RADIOS.transaction.label}
              </label>
            </FilterType>
            <FilterType
              onClick={() => selectDateFilter(FUNDED_TIMESTAMP_FILTER)}
              data-qa="Funded date"
            >
              <RadioButton
                name={DATE_FILTER_RADIOS.funded.name}
                radioOn={selectedDateFilter === FUNDED_TIMESTAMP_FILTER}
                radioFocused={focusedRadio === DATE_FILTER_RADIOS.funded.name}
                checked={selectedDateFilter === FUNDED_TIMESTAMP_FILTER}
                toggleRadio={() => selectDateFilter(FUNDED_TIMESTAMP_FILTER)}
                tabIndex="0"
                inputRef={fundedRadioRef}
                onFocus={() => setFocusedRadio(DATE_FILTER_RADIOS.funded.name)}
                onBlur={() => setFocusedRadio(null)}
                onKeyDown={handleRadioKeyDown}
              />
              <label htmlFor={radioInputId(DATE_FILTER_RADIOS.funded.name)}>
                {DATE_FILTER_RADIOS.funded.label}
              </label>
            </FilterType>
          </DateFilterRow>
          <Row className="row-calendar">
            <Col className="col col-btn" xs="12" sm="auto">
              <div className="btn-wrapper">
                {getSearchDatesPresets().map((elem, i) => (
                  <PresetButton
                    key={i}
                    outline
                    color="primary"
                    onClick={setPresetDate}
                    data-start={elem.start}
                    data-end={elem.end}
                    data-qa={elem.text}
                  >
                    {elem.text}
                  </PresetButton>
                ))}
              </div>
            </Col>

            <Col className="col col-calendar">
              <div className="calendar-wrapper">
                <div className="calendar-wrapper-inner">
                  <DayPickerRangeController
                    key={calendarKey}
                    navPrev={<FaAngleLeft />}
                    navNext={<FaAngleRight />}
                    initialVisibleMonth={() => visibleMonth}
                    onPrevMonthClick={changeMonth}
                    onNextMonthClick={changeMonth}
                    focusedInput={SEARCH_DATES.START}
                    onDatesChange={changeCalendarDates}
                    hideKeyboardShortcutsPanel={true}
                    startDate={calendar.startDate}
                    endDate={calendar.endDate}
                    enableOutsideDays={true}
                    numberOfMonths={1}
                    minimumNights={0}
                    daySize={45}
                    noBorder
                  />
                </div>
                {onResetRange ? (
                  <CalendarActionsRow>
                    <ButtonWithAction
                      variant="secondary"
                      text="Reset dates"
                      action={handleResetClick}
                      dataQa="Reset dates"
                      extraStyles="margin: 0;"
                    />
                    <ButtonWithAction
                      variant="primary"
                      text="Apply dates"
                      action={handleApplyClick}
                      dataQa="Apply dates"
                      extraStyles="margin: 0;"
                    />
                  </CalendarActionsRow>
                ) : (
                  <ClearDatesWrapper>
                    {(calendar.startDate || calendar.endDate) && (
                      <div className={"btn btn-link"} onClick={clearDates}>
                        Clear dates
                      </div>
                    )}
                  </ClearDatesWrapper>
                )}
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default DatePicker;
